import { Tooltip } from '@mm/components';
import WidgetModel, {
  QuickQuestionWidgetConfig,
} from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { errorToast, successToast } from '../../../helpers/toast';
import { useCommonWidgetHandlers } from '../../../helpers/widget-helpers/use-common-widget-handlers';
import css from './QuestionOptIn.module.css';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { MODALS_TYPE } from '@mm/state/modules/modalsState/types';
import { useSelector } from 'react-redux';
import { hasSMSNumberSelector } from '@mm/state/selectors/twillio';
import { SMSNotActiveError } from '../../../modals/QuickQuestionOptIntModal/QuickQuestionOptInModal';

type Props = {
  chatbotId: number;
  widget: WidgetModel;
  className?: string;
  disabled?: boolean;
};

const defaultText =
  'Thanks for subscribing! Msg & data rates may apply. Reply STOP at any time to unsubscribe';

export const QuestionOptIn = ({
  className,
  widget,
  disabled = false,
  chatbotId,
}: Props) => {
  const config = widget.config as QuickQuestionWidgetConfig;

  const followUpText = config.additional?.smsConfirmationMessage || '';
  const smsActivated = useSelector(hasSMSNumberSelector);

  const commonWidgetHandlers = useCommonWidgetHandlers();
  const modalActions = useModalActions();

  const updateWidgetConfig = async (
    partial: Partial<QuickQuestionWidgetConfig>,
  ) => {
    try {
      await commonWidgetHandlers.update({
        ...widget,
        config: {
          ...config,
          ...partial,
        },
      });
      successToast('Widget updated');
    } catch (err) {
      errorToast("Couldn't update widget");
      throw err;
    }
  };

  const checked = !!followUpText;

  const [reminder, setReminder] = useState(followUpText || defaultText);

  const handleUpdate = (smsConfirmationMessage = '') => {
    const update = {
      additional: { smsConfirmationMessage },
    } as Partial<QuickQuestionWidgetConfig>;

    return updateWidgetConfig(update);
  };

  const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked && !smsActivated) {
      openErrorModal();
      return;
    }

    handleUpdate(e.target.checked ? reminder : '');
  };

  const tooltipCss = useMemo(
    () => ({
      container: css.dropdown,
      content: css.content,
      arrow: css.arrow,
    }),
    [],
  );

  const openErrorModal = () => {
    modalActions.openModal({
      modalType: MODALS_TYPE.QUICK_QUESTION_OPT_IN_MODAL,
      modalProps: {
        chatbotId,
        title: 'Phone number activation',
        error: (
          <SMSNotActiveError
            chatbotId={chatbotId}
            onClick={() =>
              modalActions.closeModal(MODALS_TYPE.QUICK_QUESTION_OPT_IN_MODAL)
            }
          />
        ),
      },
    });
  };

  const openModal = () => {
    modalActions.openModal({
      modalType: MODALS_TYPE.QUICK_QUESTION_OPT_IN_MODAL,
      modalProps: {
        chatbotId,
        value: reminder,
        onSubmit: value => {
          setReminder(value);

          if (!checked) {
            return Promise.resolve(value);
          }

          return handleUpdate(value);
        },
      },
    });
  };

  return (
    <div className={classNames(className, css.container)}>
      <div className={css.field}>
        <input
          type="checkbox"
          className={css.checkbox}
          disabled={disabled}
          checked={checked}
          onChange={onCheckboxChange}
        />

        <span className={css.label}>
          <span>
            Send opt-in confirmation SMS text message
            <span className={css.icon}>
              <Tooltip
                placement="bottom"
                title={
                  <div className={css.tooltip}>
                    SMS message to be sent to user’s phone after they opt in
                  </div>
                }
              >
                <QuestionCircleOutlined />
              </Tooltip>
            </span>
          </span>
          <span className={css.action} onClick={openModal}>
            Edit confirmation message
          </span>
        </span>
      </div>
    </div>
  );
};
